// React.
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Column, usePagination, useSortBy, useTable } from "react-table";

// Third-party packages.
import * as dayjs from 'dayjs';
import TableCSVExport from '../../../component/TableCSVExport';

// Types.
import Filter from '../../../types/Filter';

// Contexts.
import { FilterContext } from '../../../context/FilterContext';

// Components.
import ReactTable from '../../../component/ReactTable';

// Helpers.
import { getDefaultPageSize, getParameter, sortStrings } from '../../../helpers/utils';
import SearchInput from '../../../component/SearchInput';
import ConditionalRender from '../../../helpers/ConditionalRender';
import { useUserRole } from '../../../context/UserRoleContext';

export default function List() {
    const { isAdmin, isDatasourceAdmin } = useUserRole();
    const { filters } = useContext(FilterContext);
    const navigate = useNavigate();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const [tableFilter, setTableFilter] = useState(query.get("filter") || "");
    const initialPageIndex = Number(query.get("page") || 0);
    const initialPageSize = Number(query.get("pageSize") || getDefaultPageSize());
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus({ preventScroll: true });
        }
    }, []);

    function clearTableFilter() {
        // Clear any pre-existing filter values.
        setTableFilter('');
    }

    function getParamValue(filter: Filter, key: string): string | null {
        return getParameter(filter.parameters, key);
    }

    function getFrom(filter: Filter): string | null {
        return getParamValue(filter, 'from');
    }

    function getTo(filter: Filter): string | null {
        return getParamValue(filter, 'to');
    }

    function getTz(filter: Filter): string | null {
        return getParamValue(filter, 'tz');
    }

    function formatDuration(ms: number): string {
        if (ms < 1000) return '0s';

        const seconds = Math.round(ms / 1000);
        const segments = [
            Math.floor(seconds / 86400),       // days
            Math.floor(seconds / 3600) % 24,   // hours
            Math.floor(seconds / 60) % 60,     // minutes
            seconds % 60                       // seconds
        ];
        const units = ['d ', 'h ', 'm ', 's'];

        return segments
                .map((value, i) => (value > 0 ? `${value}${units[i]}` : ''))
                .join('')
                .trim();
    }

    function notNull(value: string | null | undefined): string {
        return value ?? '';
    }

    const tableData = useMemo(() => {
        
        return filters
                .filter(row => {
                    const search = tableFilter.toLowerCase();
                    return (
                            row.name.toLowerCase().includes(search) ||
                            row.description.toLowerCase().includes(search) ||
                            notNull(getFrom(row)).toLowerCase().includes(search) ||
                            notNull(getTo(row)).toLowerCase().includes(search) ||
                            notNull(getTz(row)).toLowerCase().includes(search)
                    );
                })
                .map(row => {
                    const fromStr = getFrom(row);
                    const toStr = getTo(row);
                    const from = fromStr ? dayjs.default(fromStr).valueOf() : NaN;
                    const to = toStr ? dayjs.default(toStr).valueOf() : NaN;
                    const durationMs = (!isNaN(from) && !isNaN(to) && to >= from) ? to - from : NaN;
                    return {
                        ...row,
                        from: notNull(fromStr),
                        to: notNull(toStr),
                        duration: !isNaN(durationMs) ? formatDuration(durationMs) : '-',
                        durationMs,
                        timezone: notNull(getTz(row)),
                        keepdata: row.keepdata ? 'Yes' : 'No'
                    };
                })
    }, [filters, tableFilter]);

    const columns: Column[] = useMemo(() => [
        {
            accessor: 'id',
            defaultCanSort: false,
        },
        {
            Header: 'Name',
            accessor: 'name',
            className: 'fw-bold',
            defaultCanSort: true,
            sortType: sortStrings,
            Cell: ({ row, value }) => {
                const original = row.original as { description?: string };
                return (
                        <span data-tooltip-id="global-tooltip" data-tooltip-content={original.description}>
                            {value}
                        </span>
                );
            }
        },
        {
            Header: 'From',
            accessor: 'from',
            defaultCanSort: true,
        },
        {
            Header: 'To',
            accessor: 'to',
            defaultCanSort: true
        },
        {
            Header: 'Duration',
            accessor: 'duration',
            defaultCanSort: true,
            className: 'text-capitalize',
            sortType: (rowA, rowB) => {
                const a = rowA.original as { durationMs: number };
                const b = rowB.original as { durationMs: number };
                return a.durationMs - b.durationMs;
            },
            Cell: ({ cell }) => <>{cell.value}</>
        },
        {
            Header: 'Timezone',
            accessor: 'timezone',
            defaultCanSort: true
        },
        {
            Header: 'Keep Data',
            accessor: 'keepdata',
            defaultCanSort: true
        },
        {
            Header: 'Actions',
            accessor: 'actions',
            className: 'actions',
            disableSortBy: true,
            headerClassName: 'actions',
            Cell: ({ cell }) => (
                    <ConditionalRender if={isAdmin || isDatasourceAdmin}>
                        <Link to={`/admin/filters/edit/${cell.row.values.id}`} role="button"
                              className="btn btn-sm btn-primary">
                            <i className="fal fa-edit fa-fw"/><span>Edit</span>
                        </Link>
                        <Link to={`/admin/filters/delete/${cell.row.values.id}`} role="button"
                              className="btn btn-sm btn-danger">
                            <i className="fal fa-trash-alt fa-fw"/><span>Delete</span>
                        </Link>
                    </ConditionalRender>
            )
        }
    ], [isAdmin, isDatasourceAdmin]);

    useEffect(() => {
        if (tableFilter) {
            gotoPage(0);
        }
    }, [tableFilter]);

    const tableInstance = useTable({
                columns,
                data: tableData,
                initialState: {
                    hiddenColumns: ['id'],
                    pageIndex: initialPageIndex,
                    pageSize: initialPageSize,
                    sortBy: [{ id: 'name', desc: false }]
                },
                autoResetPage: false,
                autoResetSortBy: false,
                autoResetFilters: false,
                autoResetSelectedRows: false,
                autoResetExpanded: false,
                autoResetGroupBy: false,
            },
            useSortBy, usePagination);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize }
    } = tableInstance;

    

    // Transform CSV data to ensure duration and timezone are exported as strings
    const transformCsvData = useMemo(() => (csvData: any[]) => {
        return csvData.map((row: any) => {
            const transformed = { ...row };
            const original = row._original;
            
            // Ensure duration is exported as string (not parsed as date)
            // If TableCSVExport corrupted it, use the original value
            if (transformed.duration !== undefined) {
                const durationValue = transformed.duration;
                // Check if it's an invalid date string or NaN
                if (durationValue === 'Invalid Date' || durationValue === 'NaN' || 
                    (typeof durationValue === 'string' && durationValue.toLowerCase().includes('invalid'))) {
                    // Use original duration value if available
                    transformed.duration = original?.duration || '-';
                } else {
                    transformed.duration = String(durationValue || '-');
                }
            }
            
            // Ensure timezone is exported as string (not parsed as date)
            if (transformed.timezone !== undefined) {
                const timezoneValue = transformed.timezone;
                // Check if it's an invalid date string
                if (timezoneValue === 'Invalid Date' || 
                    (typeof timezoneValue === 'string' && timezoneValue.toLowerCase().includes('invalid'))) {
                    // Use original timezone value if available
                    transformed.timezone = original?.timezone || '-';
                } else {
                    transformed.timezone = String(timezoneValue || '-');
                }
            }
            
            return transformed;
        });
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        if (tableFilter) {
            params.set('filter', tableFilter);
        } else {
            params.delete('filter');
        }

        params.set('page', pageIndex.toString());
        params.set('pageSize', pageSize.toString());

        navigate({ search: params.toString() }, { replace: true });
    }, [tableFilter, pageIndex, pageSize]);


    return (
            <div className="col h-100">
                <div className="card">
                    <div className="card-header">
                        <i className="fal fa-camera-alt fa-fw mr-10" aria-hidden="true"></i>
                        Snapshots
                        <span className="badge bg-info"
                              data-tooltip-id="global-tooltip" data-tooltip-content="Total snapshot count">{filters.length.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
                        <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                           data-bs-target="#collapseFilters" aria-expanded="false" aria-controls="collapseFilters"></i>
                    </div>
                    <div id="collapseFilters" className="card-body collapse show">
                        <div className="row row-cols-1 row-cols-md-2 table-search">
                            <div className="col col-md-10">
                                <ConditionalRender if={isAdmin || isDatasourceAdmin}>
                                    <Link to="/admin/filters/create" role="button" className="btn btn-sm btn-success">
                                        <i className="fal fa-plus-square fa-fw"></i><span>Create</span>
                                    </Link>
                                </ConditionalRender>
                                <TableCSVExport
                                    tableInstance={tableInstance}
                                    filename="Snapshots"
                                    transformData={transformCsvData}
                                />
                            </div>
                            <div className="col col-md-2">
                                <SearchInput
                                    ref={inputRef}
                                    value={tableFilter}
                                    onChange={(e) => setTableFilter(e.target.value)}
                                    onClear={clearTableFilter}
                                    autoFocus
                                />
                            </div>
                        </div>
                        <ReactTable
                            getTableProps={getTableProps}
                            getTableBodyProps={getTableBodyProps}
                            headerGroups={headerGroups}
                            prepareRow={prepareRow}
                            page={page}
                            canPreviousPage={canPreviousPage}
                            canNextPage={canNextPage}
                            pageOptions={pageOptions}
                            pageCount={pageCount}
                            gotoPage={gotoPage}
                            nextPage={nextPage}
                            previousPage={previousPage}
                            setPageSize={setPageSize}
                            pageIndex={pageIndex}
                            pageSize={pageSize}
                            dataLength={tableData.length}
                            error={null}
                            loading={false}
                        />
                    </div>
                </div>
            </div>
    )
}
